import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, ScrollText, Scale, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import Page from "@/components/Page";
import { CRITERIA_PAGE } from "@/constants/testIds";

export default function SelectionCriteria() {
  const [doc, setDoc] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/criteria").then((r) => setDoc(r.data)).catch(() => {});
  }, []);

  return (
    <Page>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid={CRITERIA_PAGE.page}>
        <span className="p2p-chip"><Scale className="w-3 h-3" /> How applications are reviewed</span>
        <h1 className="font-serif mt-4 text-4xl sm:text-5xl tracking-tight" style={{ color: "#0B2D59" }}>
          Rental Selection Criteria
        </h1>
        <p className="mt-4 text-lg leading-relaxed" style={{ color: "#1F2937" }}>
          Every application is reviewed by a real person against the same written, objective criteria
          listed below. Nothing else is scored, ranked, or rated. You can read these before you start,
          and they stay the same for every applicant.
        </p>
        {doc && (
          <div className="mt-3 text-sm" style={{ color: "#475569" }} data-testid={CRITERIA_PAGE.version}>
            Criteria version {doc.version}
            {doc.updated_at ? ` · last updated ${new Date(doc.updated_at).toLocaleDateString()}` : ""}
          </div>
        )}

        <div className="p2p-card p-6 sm:p-8 mt-8">
          <div className="font-serif text-xl flex items-center gap-2" style={{ color: "#0B2D59" }}>
            <ScrollText className="w-5 h-5" /> The criteria
          </div>
          <ul className="mt-5 space-y-4" data-testid={CRITERIA_PAGE.list}>
            {(doc?.criteria || []).map((c) => (
              <li key={c.id} className="flex items-start gap-3" data-testid={CRITERIA_PAGE.item(c.id)}>
                <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "#00A7B5" }} />
                <span className="text-base leading-relaxed" style={{ color: "#0B2D59" }}>{c.text}</span>
              </li>
            ))}
            {!doc && <li className="text-sm" style={{ color: "#475569" }}>Loading criteria…</li>}
          </ul>
        </div>

        <div className="p2p-card p-6 sm:p-8 mt-6">
          <div className="font-serif text-xl flex items-center gap-2" style={{ color: "#0B2D59" }}>
            <ShieldCheck className="w-5 h-5" /> What is not part of the review
          </div>
          <ul className="mt-4 space-y-2 text-base leading-relaxed" style={{ color: "#1F2937" }}>
            <li>· No credit check.</li>
            <li>· No criminal background check.</li>
            <li>· The optional &quot;Your Story&quot; section is voluntary. It is never scored and is never
              used to approve, deny, rank, or judge an application. It is read only after the objective
              review is complete, and only for neutral clarification and respectful communication.</li>
            <li>· Race, color, religion, national origin, sex, sexual orientation, gender identity,
              disability, familial status, and every other protected characteristic play no role in review.</li>
          </ul>
          <p className="mt-4 text-sm" style={{ color: "#475569" }}>
            When more than one applicant meets the criteria, selection is based on neutral factors such as
            application completion date, unit availability, or a transparent waiting list. This application
            is designed to give people a fair and respectful way to apply — you are more than your paperwork.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button
            className="p2p-btn-primary inline-flex items-center gap-2"
            data-testid={CRITERIA_PAGE.startApplyBtn}
            onClick={() => navigate("/apply")}
          >
            Start application <ArrowRight className="w-4 h-4" />
          </button>
          <span className="text-sm" style={{ color: "#475569" }}>Equal Housing Opportunity.</span>
        </div>
      </div>
    </Page>
  );
}
